import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useSnackbar } from 'components/common/SnackbarNotification';
import {
  EntityFormModal,
  EntityViewModal,
} from 'components/modals/modules/admin-module/EntityMasterModal';
import type {
  EntityFormData,
  EntityRecord,
} from 'components/modals/modules/admin-module/EntityMasterModal';
import { formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useEntities, useCreateEntity, useUpdateEntity, useDeleteEntity } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';
// ============================================================================
// INITIAL FORM DATA
// ============================================================================

const emptyFormData: EntityFormData = {
  entityCode: '',
  entityName: '',
  entityType: 'COMPANY',
  description: '',
};

// ============================================================================
// MAIN ENTITY MASTER SCREEN
// ============================================================================

const EntityMasterScreen: React.FC = () => {
  // API hooks
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useEntities();
  const { createEntity } = useCreateEntity();
  const { updateEntity } = useUpdateEntity();
  const { deleteEntity } = useDeleteEntity();

  // Data from API
  const entities = (apiData as unknown as EntityRecord[]) || [];
  

  // UI state
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [, setSelectedRows] = useState<EntityRecord[]>([]);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<EntityRecord | null>(null);
  const [formData, setFormData] = useState<EntityFormData>(emptyFormData);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', variant: 'default', onConfirm: () => {} });

  const { showMessage } = useSnackbar();

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() => {
    return entities.filter((entity) => {
      const matchesSearch = !searchQuery ||
        entity.entityCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entity.entityName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (entity.description || '').toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === '' ||
        (statusFilter === 'Active' && entity.isActive) ||
        (statusFilter === 'Inactive' && !entity.isActive);

      const matchesType = typeFilter === '' || entity.entityType === typeFilter;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [entities, searchQuery, statusFilter, typeFilter]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleAddNew = useCallback(() => {
    setFormMode('create');
    setSelectedRecord(null);
    setFormData(emptyFormData);
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback((record: EntityRecord) => {
    setFormMode('edit');
    setSelectedRecord(record);
    setFormData({
      entityCode: record.entityCode,
      entityName: record.entityName,
      entityType: record.entityType,
      description: record.description || '',
    });
    setFormModalOpen(true);
  }, []);

  const handleView = useCallback((record: EntityRecord) => {
    setSelectedRecord(record);
    setViewModalOpen(true);
  }, []);

  const handleSaveForm = useCallback(() => {
    const action = formMode === 'create' ? 'Create' : 'Save';
    const message = formMode === 'create'
      ? 'Are you sure you want to create this entity?'
      : 'Are you sure you want to save changes to this entity?';

    setConfirmDialog({
      open: true,
      title: `${action} Entity`,
      message,
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          if (formMode === 'create') {
              await createEntity({
                entityCode: formData.entityCode,
                entityName: formData.entityName,
                entityType: formData.entityType,
                description: formData.description || undefined,
              });
              await refetch();
            } else if (selectedRecord) {
              await updateEntity(selectedRecord.id, {
                entityCode: formData.entityCode,
                entityName: formData.entityName,
                entityType: formData.entityType,
                description: formData.description || undefined,
                rowVersion: selectedRecord.rowVersion,
              });
              await refetch();
            }

          showMessage(`Entity ${formMode === 'create' ? 'created' : 'updated'} successfully`, 'success');
          setFormModalOpen(false);
        } catch (err: any) {
          const apiMessage = err?.response?.data?.message;
          showMessage(apiMessage || `Failed to ${formMode === 'create' ? 'create' : 'update'} entity`, 'error');
        }
      },
    });
  }, [formMode, formData, entities.length, selectedRecord, showMessage, createEntity, updateEntity, refetch]);

  const handleDelete = useCallback((record: EntityRecord) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Entity',
      message: `Are you sure you want to delete entity "${record.entityName}" (${record.entityCode})?`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          await deleteEntity(record.id);
            await refetch();

          showMessage('Entity deleted successfully', 'success');
        } catch (err: any) {
          const apiMessage = err?.response?.data?.message;
          showMessage(apiMessage || 'Failed to delete entity', 'error');
        }
      },
    });
  }, [showMessage, deleteEntity, refetch]);

  // ============================================================================
  // ROW ACTIONS
  // ============================================================================

  const getRowActions = useCallback((row: EntityRecord): ActionMenuItem[] => [
    {
      id: 'view',
      label: 'View Details',
      icon: <IconSystem.eye size={18} />,
      onClick: () => handleView(row),
    },
    {
      id: 'edit',
      label: 'Edit',
      icon: <IconSystem.edit size={18} />,
      onClick: () => handleEdit(row),
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: <IconSystem.trash size={18} />,
      onClick: () => handleDelete(row),
      variant: 'danger' as const,
    },
  ], [handleView, handleEdit, handleDelete]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<EntityRecord>[] = useMemo(() => [
    { id: 'entityCode', header: 'Entity Code', accessorKey: 'entityCode', size: 130 },
    { id: 'entityName', header: 'Entity Name', accessorKey: 'entityName', size: 220 },
    { id: 'entityType', header: 'Type', accessorKey: 'entityType', size: 150 },
    {
      id: 'isActive',
      header: 'Status',
      accessorKey: 'isActive',
      size: 110,
      cell: ({ getValue }: any) => {
        const isActive = getValue();
        return (
          <span style={getStatusBadgeStyle(isActive ? 'ACTIVE' : 'INACTIVE')}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
    {
      id: 'description',
      header: 'Description',
      accessorKey: 'description',
      size: 200,
      cell: ({ getValue }: any) => getValue() || '—',
    },
    {
      id: 'createdAt',
      header: 'Created At',
      accessorKey: 'createdAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], []);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add New Entity',
      onClick: handleAddNew,
      tooltip: 'Add New Entity',
      variant: 'icon',
      visible: true,
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Entity Master"
        icon={<IconSystem.folder size={20} />}
        description="Manage companies and legal entities"
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search entities...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      <div className="module-content">
        {/* Error */}
        {apiError && entities.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load entities</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && entities.length === 0 ? (
          <GridLoadingSkeleton columns={5} />
        ) : (
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={filteredData}
                columns={tableColumns}
                rowActions={getRowActions}
                enableRowSelection
                enablePagination
                enableColumnFilters={false}
                onSelectionChange={setSelectedRows}
                emptyMessage="No entities found"
              />
            )}
          </div>

          {/* Filter Panel */}
          <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>

            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={[
                    { value: '', label: 'All Status' },
                    { value: 'Active', label: 'Active' },
                    { value: 'Inactive', label: 'Inactive' },
                  ]}
                  value={statusFilter}
                  onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                  placeholder="Search status..."
                  isSearchable={true}
                  isClearable={false}
                  size="sm"
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Entity Type"
                  options={[
                    { value: '', label: 'All Types' },
                    { value: 'COMPANY', label: 'Company' },
                    { value: 'HOLDING_COMPANY', label: 'Holding Company' },
                  ]}
                  value={typeFilter}
                  onChange={(val: string | string[] | null) => setTypeFilter((val as string) || '')}
                  placeholder="Search types..."
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setStatusFilter(''); setTypeFilter(''); }}>
                Clear
              </button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>
                Apply
              </button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Form Modal */}
      <EntityFormModal
        isOpen={formModalOpen}
        mode={formMode}
        formData={formData}
        onFormDataChange={setFormData}
        onSave={handleSaveForm}
        onClose={() => setFormModalOpen(false)}
      />

      {/* View Modal */}
      <EntityViewModal
        isOpen={viewModalOpen}
        record={selectedRecord}
        onClose={() => setViewModalOpen(false)}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        confirmLabel={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
      />
    </div>
  );
};

export default EntityMasterScreen;
